
import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { UserDropdown } from "@/components/auth/UserDropdown";
import { useBranding } from "@/contexts/BrandingContext";
import {
  BarChart3,
  Database,
  Map,
  Calculator,
  TrendingUp,
  DollarSign,
  FileText,
  Settings,
  PieChart,
  CreditCard,
  Briefcase,
  Bot,
  Users,
  Menu,
  Home,
} from "lucide-react";

interface DashboardShellProps {
  children: React.ReactNode;
  activePage: string;
  onNavigate: (page: string) => void;
}

const navigationItems = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: Home,
  },
  {
    id: "data-input",
    label: "Data Input",
    icon: Database,
  },
  {
    id: "tenements",
    label: "Tenements",
    icon: Map,
  },
  {
    id: "jorc-valuation",
    label: "JORC Valuation",
    icon: PieChart,
  },
  {
    id: "forecast",
    label: "Forecast Models",
    icon: TrendingUp,
  },
  {
    id: "calculator",
    label: "NPV Calculator",
    icon: Calculator,
  },
  {
    id: "capital",
    label: "Capital Management",
    icon: CreditCard,
  },
  {
    id: "reports",
    label: "Reports",
    icon: FileText,
  },
  {
    id: "balance-sheet",
    label: "Balance Sheet",
    icon: BarChart3,
  },
  {
    id: "financial-assumptions",
    label: "Financial Assumptions",
    icon: DollarSign,
  },
  {
    id: "ai-dashboard",
    label: "AI Financial Dashboard",
    icon: Bot,
  },
  {
    id: "admin",
    label: "Admin Panel",
    icon: Users,
  },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
  },
];

export function DashboardShell({ children, activePage, onNavigate }: DashboardShellProps) {
  const { branding } = useBranding();
  const [open, setOpen] = useState(false);

  const NavContent = () => (
    <div className="space-y-4 py-4">
      <div className="px-3 py-2">
        <div className="flex items-center gap-2 mb-4">
          <img src={branding.logoUrl} alt={branding.name} className="h-8 w-auto" />
          <h2 className="text-lg font-semibold">{branding.name}</h2>
        </div>
        <div className="space-y-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            return (
              <Button
                key={item.id}
                variant={activePage === item.id ? "secondary" : "ghost"}
                className={cn(
                  "w-full justify-start",
                  activePage === item.id && "bg-secondary"
                )}
                onClick={() => {
                  onNavigate(item.id);
                  setOpen(false);
                }}
              >
                <Icon className="mr-2 h-4 w-4" />
                {item.label}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="grid min-h-screen w-full md:grid-cols-[220px_1fr] lg:grid-cols-[280px_1fr]">
      <div className="hidden border-r bg-muted/40 md:block">
        <div className="flex h-full max-h-screen flex-col gap-2">
          <ScrollArea className="flex-1">
            <NavContent />
          </ScrollArea>
        </div>
      </div>
      <div className="flex flex-col">
        <header className="flex h-14 items-center gap-4 border-b bg-muted/40 px-4 lg:h-[60px] lg:px-6">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="shrink-0 md:hidden">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle navigation menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex flex-col">
              <ScrollArea className="flex-1">
                <NavContent />
              </ScrollArea>
            </SheetContent>
          </Sheet>
          <div className="w-full flex-1">
            <h1 className="text-lg font-semibold">
              {navigationItems.find(item => item.id === activePage)?.label || "Dashboard"}
            </h1>
          </div>
          <UserDropdown />
        </header>
        <main className="flex flex-1 flex-col gap-4 p-4 lg:gap-6 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
